/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Auth Store - Zustand State Management
 * ============================================================================
 *
 * Centralized authentication state with token management and session handling.
 *
 * ============================================================================
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { projectConfig } from '@/config/project.config';
import { isJwtExpired, getJwtExpiry } from '@/lib/utils';

/**
 * Effective inactivity timeout, in minutes.
 *
 * Organisations can override this via the `session_timeout_minutes` org setting.
 * The store cannot call React hooks to read that setting, so useSessionMonitor
 * pushes the resolved value in here once org settings load; until then (and for
 * orgs that never override it) the project default applies.
 */
let effectiveInactivityMinutes = projectConfig.auth.session.inactivityTimeoutMinutes;

export function setSessionInactivityMinutes(minutes: number | undefined | null): void {
  effectiveInactivityMinutes =
    typeof minutes === 'number' && minutes > 0
      ? minutes
      : projectConfig.auth.session.inactivityTimeoutMinutes;
}

export function getSessionInactivityMs(): number {
  return effectiveInactivityMinutes * 60 * 1000;
}

/**
 * User entity - aligned with API UserDto
 */
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  orgId: string;
  orgName?: string;
  orgCurrency?: string;
  orgCountry?: string;
  displayName?: string;
  avatarUrl?: string;
  role: string;
  mfaEnabled?: boolean;
  lastLoginAt?: string;
  /**
   * Only today's financial figures are visible to this user. Null/absent from
   * any response that could not resolve it — treated as restricted by
   * `isDayScopeRestriction`, so an unresolved flag never grants visibility.
   */
  currentDayRestricted?: boolean | null;
}

/**
 * Auth state interface
 */
interface AuthState {
  // State
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  lastActivity: number | null;
  sessionExpiresAt: number | null;
  permissions: string[];

  // Actions
  login: (user: User, accessToken: string, refreshToken?: string) => void;
  setAuth: (user: User, token: string) => void;
  setTokens: (accessToken: string, refreshToken?: string) => void;
  updateUser: (userData: Partial<User>) => void;
  setPermissions: (permissions: string[]) => void;
  updateLastActivity: () => void;
  setLoading: (isLoading: boolean) => void;
  logout: () => void;
  reset: () => void;

  // Computed/Helpers
  isSessionValid: () => boolean;
  hasRole: (role: string) => boolean;
  hasPermission: (permission: string) => boolean;
  getFullName: () => string;
}

/**
 * Initial state
 */
const initialState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  lastActivity: null,
  sessionExpiresAt: null,
  permissions: [] as string[],
};

/**
 * Calculate session expiry from JWT token or fallback to config
 * Reads the 'exp' claim from the JWT token for accurate expiry
 */
const calculateSessionExpiry = (accessToken: string | null): number => {
  // Try to read expiry from JWT token
  const jwtExpiry = getJwtExpiry(accessToken);
  if (jwtExpiry) {
    return jwtExpiry;
  }

  // Fallback to config-based absolute timeout
  const absoluteTimeout = projectConfig.auth.session.absoluteTimeoutMinutes * 60 * 1000;
  return Date.now() + absoluteTimeout;
};

/**
 * Auth store
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      ...initialState,

      /**
       * Login with user data and tokens
       */
      login: (user, accessToken, refreshToken) => {
        // Clear stale React Query cache from previous session
        import('@/main').then(m => m.queryClient.clear()).catch(() => {});
        import('@/lib/pwa/persister').then(m => m.clearPersistedQueryCache()).catch(() => {});

        const now = Date.now();
        set({
          user,
          accessToken,
          refreshToken: refreshToken || null,
          isAuthenticated: true,
          isLoading: false,
          lastActivity: now,
          sessionExpiresAt: calculateSessionExpiry(accessToken),
        });
      },

      /**
       * Set auth (legacy support)
       */
      setAuth: (user, accessToken) => {
        const now = Date.now();
        set({
          user,
          accessToken,
          isAuthenticated: true,
          lastActivity: now,
          sessionExpiresAt: calculateSessionExpiry(accessToken),
        });
      },

      /**
       * Update tokens (e.g., after refresh)
       */
      setTokens: (accessToken, refreshToken) => {
        set((state) => ({
          accessToken,
          refreshToken: refreshToken || state.refreshToken,
          lastActivity: Date.now(),
          sessionExpiresAt: calculateSessionExpiry(accessToken),
        }));
      },

      /**
       * Partial user update
       */
      updateUser: (userData) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        }));
      },

      /**
       * Store the user's effective permission codes (e.g., ["DASHBOARD:VIEW", "SALES:CREATE"])
       */
      setPermissions: (permissions) => {
        set({ permissions });
      },

      /**
       * Update last activity timestamp
       */
      updateLastActivity: () => {
        set({ lastActivity: Date.now() });
      },

      /**
       * Set loading state
       */
      setLoading: (isLoading) => {
        set({ isLoading });
      },

      /**
       * Logout and clear all auth data
       */
      logout: () => {
        set(initialState);
        // Clear any other auth-related storage
        localStorage.removeItem('corex-device-fingerprint');
        import('@/main').then(m => m.queryClient.clear()).catch(() => {});
        import('@/lib/pwa/persister').then(m => m.clearPersistedQueryCache()).catch(() => {});
      },

      /**
       * Reset store to initial state
       */
      reset: () => {
        set(initialState);
      },

      /**
       * Check if session is still valid
       */
      isSessionValid: () => {
        const state = get();

        if (!state.isAuthenticated || !state.accessToken) {
          return false;
        }

        // Check if JWT token is expired (with 30 second buffer)
        if (isJwtExpired(state.accessToken, 30)) {
          return false;
        }

        // Check absolute session expiry
        if (state.sessionExpiresAt && Date.now() > state.sessionExpiresAt) {
          return false;
        }

        // Check inactivity timeout
        if (state.lastActivity) {
          if (Date.now() - state.lastActivity > getSessionInactivityMs()) {
            return false;
          }
        }

        return true;
      },

      /**
       * Check if user has specific role
       */
      hasRole: (role) => {
        const state = get();
        return state.user?.role === role;
      },

      /**
       * Check if user has a specific permission code (e.g., "SALES:CREATE").
       * SUPER_ADMIN/ORG_ADMIN bypass. Optimistic while permissions load.
       */
      hasPermission: (permission) => {
        const state = get();
        const role = state.user?.role;
        if (role === 'SUPER_ADMIN' || role === 'ORG_ADMIN') return true;
        if (state.permissions.length === 0) return true;
        return state.permissions.includes(permission);
      },

      /**
       * Get user's full name
       */
      getFullName: () => {
        const state = get();
        if (!state.user) return '';
        return `${state.user.firstName} ${state.user.lastName}`.trim();
      },
    }),
    {
      name: 'corex-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        lastActivity: state.lastActivity,
        sessionExpiresAt: state.sessionExpiresAt,
        permissions: state.permissions,
      }),
    }
  )
);

/**
 * Selector hooks for common use cases
 */
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAccessToken = () => useAuthStore((state) => state.accessToken);
export const usePermissions = () => useAuthStore((state) => state.permissions);
